/**
 * Where the site navigation goes: one list per menu, read by the top bar
 * (Navigation.tsx), the sidebar (Sidebar.tsx) and the «سجل معنا» menu
 * (RegisterMenu.tsx), so a destination is defined once.
 *
 * Every label is an i18n key with its namespace (`nav:` for the new nav.ts,
 * `common:` for the existing navigation keys), so callers just `t(item.labelKey)`.
 * Icons are Remix outline weights (THEME.md §8 rule 1), and no two items in the
 * same menu share an icon.
 *
 * Feature flags mirror their backend flags: a link only appears once its
 * surface is live. They are read from the build env, so production builds use
 * Apps/web/.env (NAV_CARD_GRID, INVESTMENT_HUB and PROFESSIONALS_DIRECTORY are
 * set only in .env.development today).
 */
import type { AuthUser } from '../../../hooks/useAuth';

export const BROKER_MAP_ENABLED = import.meta.env.VITE_BROKER_MAP_ENABLED === 'true';
export const PROFESSIONALS_DIRECTORY_ENABLED = import.meta.env.VITE_PROFESSIONALS_DIRECTORY_ENABLED === 'true';
export const INVESTMENT_HUB_ENABLED = import.meta.env.VITE_INVESTMENT_HUB_ENABLED === 'true';
export const SERVICE_WORKFLOWS_ENABLED = import.meta.env.VITE_SERVICE_WORKFLOWS_ENABLED === 'true';
export const ADS_BOARD_ENABLED = import.meta.env.VITE_ADS_BOARD_ENABLED === 'true';
export const PRO_ROLES_ENABLED = import.meta.env.VITE_PRO_ROLES_ENABLED === 'true';
/** Card-grid (2-col mega) dropdowns with descriptions; off falls back to plain lists. */
export const NAV_CARD_GRID_ENABLED = import.meta.env.VITE_NAV_CARD_GRID_ENABLED === 'true';

export interface NavItem {
  /** Route, query included. Unique within its list, so it doubles as the React key. */
  to: string;
  /** Remix icon class. */
  icon: string;
  /** Namespaced i18n key of the label. */
  labelKey: string;
  /** Namespaced i18n key of the one-line description (card-grid menus only). */
  descKey?: string;
}

// For Rent / For Sale. The params are exactly what the search page's own filters
// produce (pages/search/page.tsx), so results match in-page filtering.
export const RENT_ITEMS: readonly NavItem[] = [
  { to: '/search?listingType=rent', icon: 'ri-apps-line', labelKey: 'nav:browse.all', descKey: 'nav:browse.rentAllDesc' },
  { to: '/search?listingType=rent&category=residential', icon: 'ri-home-4-line', labelKey: 'nav:browse.residential', descKey: 'nav:browse.rentResidentialDesc' },
  { to: '/search?listingType=rent&category=commercial', icon: 'ri-store-2-line', labelKey: 'nav:browse.commercial', descKey: 'nav:browse.commercialDesc' },
];

export const BUY_ITEMS: readonly NavItem[] = [
  { to: '/search?listingType=buy', icon: 'ri-apps-line', labelKey: 'nav:browse.all', descKey: 'nav:browse.buyAllDesc' },
  { to: '/search?listingType=buy&category=residential', icon: 'ri-home-4-line', labelKey: 'nav:browse.residential', descKey: 'nav:browse.buyResidentialDesc' },
  { to: '/search?listingType=buy&category=commercial', icon: 'ri-store-2-line', labelKey: 'nav:browse.commercial', descKey: 'nav:browse.commercialDesc' },
];

// For Investment. The 4-journey hub replaces the legacy pair once
// VITE_INVESTMENT_HUB_ENABLED is on. Special deals lives here and nowhere else.
const INVEST_ITEMS_LEGACY: readonly NavItem[] = [
  { to: '/investors', icon: 'ri-building-4-line', labelKey: 'nav:invest.hub', descKey: 'nav:invest.hubDesc' },
  { to: '/special-deals', icon: 'ri-briefcase-4-line', labelKey: 'nav:invest.specialDeals', descKey: 'nav:invest.specialDealsDesc' },
];
const INVESTMENT_HUB_ITEMS: readonly NavItem[] = [
  { to: '/investors/licensed-projects', icon: 'ri-building-3-line', labelKey: 'nav:invest.licensedProjects', descKey: 'nav:invest.licensedProjectsDesc' },
  { to: '/investors/contributions', icon: 'ri-checkbox-circle-line', labelKey: 'nav:invest.contributions', descKey: 'nav:invest.contributionsDesc' },
  { to: '/investors/requests', icon: 'ri-search-eye-line', labelKey: 'nav:invest.requests', descKey: 'nav:invest.requestsDesc' },
  { to: '/investors/list', icon: 'ri-coins-line', labelKey: 'nav:invest.list', descKey: 'nav:invest.listDesc' },
];
export const INVEST_ITEMS: readonly NavItem[] = INVESTMENT_HUB_ENABLED ? INVESTMENT_HUB_ITEMS : INVEST_ITEMS_LEGACY;

export const NON_SAUDI_ITEM: NavItem = { to: '/projects-non-saudis', icon: 'ri-earth-line', labelKey: 'nav:top.nonSaudi' };

// "Find a professional". The unified directory adds three licensed professional
// types when its flag is on; the broker/marketer row is not repeated there.
// Consultants stay hidden from the public directory (owner request).
const PROFESSIONAL_DIRECTORY_ITEMS: readonly NavItem[] = [
  { to: '/professionals?type=PROPERTY_MANAGEMENT', icon: 'ri-building-2-line', labelKey: 'nav:findPro.propertyManagement', descKey: 'nav:findPro.propertyManagementDesc' },
  { to: '/professionals?type=FACILITIES_MANAGEMENT', icon: 'ri-tools-line', labelKey: 'nav:findPro.facilitiesManagement', descKey: 'nav:findPro.facilitiesManagementDesc' },
  { to: '/professionals?type=APPRAISER', icon: 'ri-scales-3-line', labelKey: 'nav:findPro.appraisers', descKey: 'nav:findPro.appraisersDesc' },
];
export const FIND_PRO_ITEMS: readonly NavItem[] = [
  { to: '/trusted-brokers?type=individual', icon: 'ri-shield-check-line', labelKey: 'nav:findPro.brokers', descKey: 'nav:findPro.brokersDesc' },
  { to: '/trusted-brokers?type=developer', icon: 'ri-community-line', labelKey: 'nav:findPro.developers', descKey: 'nav:findPro.developersDesc' },
  ...(PROFESSIONALS_DIRECTORY_ENABLED ? PROFESSIONAL_DIRECTORY_ITEMS : []),
  ...(BROKER_MAP_ENABLED
    ? [{ to: '/brokers/map', icon: 'ri-road-map-line', labelKey: 'nav:findPro.brokersMap', descKey: 'nav:findPro.brokersMapDesc' }]
    : []),
];

// «سجل معنا»: the professional sign-ups. Each deep-links into the intent-first
// signup with that role preselected (?role= is read by pages/register/page.tsx →
// IntentSignupFlow); the values are backend ActivityType members (roleCatalog.ts).
// Consultant is omitted for now (owner request).
export const CREATE_ACCOUNT_ITEM: NavItem = {
  to: '/register',
  icon: 'ri-user-add-line',
  labelKey: 'nav:register.createAccount',
  descKey: 'nav:register.createAccountDesc',
};
export const REGISTER_PRO_ITEMS: readonly NavItem[] = [
  { to: '/register?role=DEVELOPER', icon: 'ri-building-4-line', labelKey: 'nav:register.developer', descKey: 'nav:register.developerDesc' },
  { to: '/register?role=BROKER', icon: 'ri-key-2-line', labelKey: 'nav:register.broker', descKey: 'nav:register.brokerDesc' },
  { to: '/register?role=PROPERTY_MANAGEMENT', icon: 'ri-building-2-line', labelKey: 'nav:register.propertyManagement', descKey: 'nav:register.propertyManagementDesc' },
  { to: '/register?role=FACILITIES_MANAGEMENT', icon: 'ri-tools-line', labelKey: 'nav:register.facilitiesManagement', descKey: 'nav:register.facilitiesManagementDesc' },
  { to: '/register?role=APPRAISER', icon: 'ri-scales-3-line', labelKey: 'nav:register.appraiser', descKey: 'nav:register.appraiserDesc' },
];

// Services.
export const POST_REQUEST_ITEM: NavItem = {
  to: '/client-requests/new',
  icon: 'ri-megaphone-line',
  labelKey: 'common:navigation.postRequest',
  descKey: 'nav:services.postRequestDesc',
};
export const SUBSCRIPTIONS_ITEM: NavItem = {
  to: '/membership',
  icon: 'ri-vip-crown-line',
  labelKey: 'common:navigation.sidebar.subscriptions',
  descKey: 'nav:services.subscriptionsDesc',
};
export const MARKET_TOOLS_ITEM: NavItem = {
  to: '/market-tools',
  icon: 'ri-bar-chart-box-line',
  labelKey: 'nav:services.marketTools',
  descKey: 'nav:services.marketToolsDesc',
};
export const PRO_SERVICES_ITEM: NavItem = { to: '/service-engagements', icon: 'ri-tools-line', labelKey: 'nav:services.proServices' };
/** The Requests board: professional accounts only (owner decision; enforced by the backend too). */
export const REQUESTS_BOARD_ITEM: NavItem = { to: '/advertisements', icon: 'ri-clipboard-line', labelKey: 'nav:services.requestsBoard' };
export const ADDONS_ITEM: NavItem = { to: '/addons', icon: 'ri-puzzle-line', labelKey: 'nav:services.addons' };
export const BUNDLES_ITEM: NavItem = { to: '/bundles', icon: 'ri-gift-line', labelKey: 'nav:services.bundles' };
export const COINS_ITEM: NavItem = { to: '/coins', icon: 'ri-copper-coin-line', labelKey: 'nav:services.coins' };
export const MY_ADDONS_ITEM: NavItem = { to: '/my-addons', icon: 'ri-shopping-bag-line', labelKey: 'nav:services.myAddons' };

// Help & support (the old About menu). The top bar has no About menu; these
// live in the sidebar and the footer.
export const HELP_ITEMS: readonly NavItem[] = [
  { to: '/about', icon: 'ri-information-line', labelKey: 'common:navigation.sidebar.aboutUs' },
  { to: '/contact', icon: 'ri-phone-line', labelKey: 'common:navigation.contact' },
  { to: '/complaints', icon: 'ri-feedback-line', labelKey: 'common:navigation.sidebar.complaints' },
];

/** Pages where returning "back here" after signing in makes no sense. */
const AUTH_PATHS = ['/signin', '/register', '/reset-password'];

/**
 * Router state for a «تسجيل الدخول» link: the sign-in page sends the user back
 * to where they were (it reads `state.from`). Nothing is passed from the auth
 * pages themselves, so signing in never lands back on a sign-in or sign-up form.
 */
export function signInState(location: { pathname: string; search?: string; hash?: string }):
  | { from: { pathname: string; search: string; hash: string } }
  | undefined {
  const { pathname } = location;
  if (AUTH_PATHS.some((path) => pathname === path || pathname.startsWith(`${path}/`))) return undefined;
  return { from: { pathname, search: location.search ?? '', hash: location.hash ?? '' } };
}

/** The name to show for a signed-in user: full name, first + last, then the e-mail's local part. */
export function userDisplayName(
  user: Pick<AuthUser, 'fullName' | 'firstName' | 'lastName' | 'email'> | null | undefined,
): string {
  if (!user) return '';
  if (user.fullName && user.fullName.trim().length > 0) return user.fullName.trim();
  const composed = `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();
  if (composed.length > 0) return composed;
  return user.email?.split('@')[0] ?? '';
}

/** A broker or company account: lists through /properties/new and sees the advertiser links. */
export function isAdvertiserAccount(user: Pick<AuthUser, 'accountType'> | null | undefined): boolean {
  return user?.accountType === 'broker' || user?.accountType === 'company';
}

/**
 * Who sees the subscription plans link: guests, advertisers and staff. Signed-in
 * individuals are sent to /addons by AdvertiserRoute, so the link is hidden for them.
 */
export function canSeeSubscriptions(user: Pick<AuthUser, 'accountType' | 'role'> | null | undefined): boolean {
  return !user || isAdvertiserAccount(user) || user.role === 'ADMIN' || user.role === 'SUPER_ADMIN';
}

export type AccountBadgeTone = 'company' | 'broker' | 'neutral';

/**
 * The short account-type badge under the user's name. An individual who has
 * listed under Safaqat 24's FAL licence gets that badge; a plain individual
 * gets «فرد» only when `includeIndividual` is set (the sidebar card), because
 * in the compact user menu it is noise. A developer gets its own badge (it
 * used to fall through to "Individual").
 */
export function accountBadge(
  user: Pick<AuthUser, 'accountType' | 'hasFalListing'> | null | undefined,
  { includeIndividual = false }: { includeIndividual?: boolean } = {},
): { labelKey: string; tone: AccountBadgeTone } | null {
  switch (user?.accountType) {
    case 'company':
      return { labelKey: 'nav:badges.company', tone: 'company' };
    case 'broker':
      return { labelKey: 'nav:badges.broker', tone: 'broker' };
    case 'developer':
      return { labelKey: 'nav:badges.developer', tone: 'company' };
    case 'individual':
      if (user.hasFalListing) return { labelKey: 'common:badges.falIndividual', tone: 'company' };
      return includeIndividual ? { labelKey: 'nav:badges.individual', tone: 'neutral' } : null;
    default:
      return null;
  }
}
